'use client';

import Link from 'next/link';
import { useEffect, useState, type CSSProperties, type ReactNode, type SyntheticEvent } from 'react';
import { useSiteTheme } from '@/components/SiteThemeProvider';
import UiIcon from '@/components/ui/UiIcon';
import { usePublicProjects } from '@/lib/swr/hooks/useProjects';
import { useNews } from '@/lib/swr/hooks/useNews';
import { getNewsDisplayMeta } from '@/utils/newsBlocksMeta';
import { achievementNewsTag, directions, directionNewsTags, getAchievementNews, getDirectionNews, getDirectionProjects, getNewsPublicationTime, type DirectionId } from './directions';
import type { WallPhotoCategory } from '@/lib/homeWallPhotos';
import styles from './direction-content.module.css';
import { useWallPhotos } from './useWallPhotos';
import WallFrame from './WallFrame';

export default function DirectionContent({ direction, title }: { direction: DirectionId; title: string }) {
  const projectFeed = usePublicProjects(24, direction);
  const projects = getDirectionProjects(direction, projectFeed.projects);
  const projectCount = !projects.length && (projectFeed.isLoading || projectFeed.error)
    ? '—' : String(projects.length).padStart(2, '0');

  return (
    <div className={styles.content}>
      <section className={styles.projectsPanel} data-direction-projects={direction} aria-labelledby={`${direction}-projects-title`} aria-busy={projectFeed.isLoading}>
        <ProjectGlass>
          <div className={styles.projectMeta} role="status" aria-label={`Проектов: ${projectCount}`}>
            <span className={styles.projectMetaIcon}><UiIcon name={directions.find(item => item.id === direction)!.icon} /></span>
            <span><span className={styles.projectCountLabel}>Проектов: </span><strong>{projectCount}</strong></span>
          </div>
          <div className={styles.heading}>
            <h2 id={`${direction}-projects-title`}>{title}</h2>
            <Link href="/projects" className={styles.allLink}>Все проекты<span className={styles.allLinkIcon}><UiIcon name="ArrowUpRight" /></span></Link>
          </div>
          <div key={direction} className={styles.projectsScroll} tabIndex={0} role="region" aria-label="Список проектов направления" onWheel={event => event.stopPropagation()}>
            {projectFeed.isLoading && !projectFeed.error && <span className={styles.srOnly} role="status">Загружаем проекты…</span>}
            {!projects.length && !projectFeed.isLoading && !projectFeed.error &&
              <p className={styles.notice}>Публичных проектов в этом направлении пока нет.</p>}
            {projects.length > 0 && (
              <ol className={styles.projects}>
                {projects.map((project, index) => (
                  <li key={project.id}>
                    <Link href={`/projects/${project.id}`} className={styles.projectLink}>
                      <span className={styles.projectNumber} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                      <span className={styles.projectCopy}>
                        <span className={styles.projectTitle}>{project.name}</span>
                      </span>
                      <UiIcon name="ArrowUpRight" className={styles.arrow} />
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </ProjectGlass>
      </section>
    </div>
  );
}

export function WallLighting() {
  return (
    <div className={`${styles.newsWall} ${styles.wallLighting}`} aria-hidden="true">
      {[4, 34.7, 65.3, 96].map(position => <span key={position} className={styles.wallLamp} style={{ left: `${position}%` }} />)}
    </div>
  );
}

function ProjectGlass({ children }: { children: ReactNode }) {
  const { theme } = useSiteTheme();
  const variants = { light: 'day', gray: 'dawn', dark: 'night' } as const;
  const variant = variants[theme];
  const base = `/assets/home-projects-glass/${variant}-v9.png`;
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const [failedSources, setFailedSources] = useState<Record<string, boolean>>({});
  const [visibleTheme, setVisibleTheme] = useState(theme);
  useEffect(() => { if (ready[base] || failedSources[base]) setVisibleTheme(theme); }, [base, failedSources, ready, theme]);
  const loaded = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const src = img.getAttribute('src')!;
    // Не включаем недекодированную подсветку: при сбое остаётся обычная панель.
    void img.decode().then(() => {
      setReady(previous => ({ ...previous, [src]: true }));
      setFailedSources(previous => ({ ...previous, [src]: false }));
    }, () => {
      setReady(previous => ({ ...previous, [src]: false }));
      setFailedSources(previous => ({ ...previous, [src]: true }));
    });
  };
  const failed = (event: SyntheticEvent<HTMLImageElement>) => {
    const src = event.currentTarget.getAttribute('src')!;
    setReady(previous => ({ ...previous, [src]: false }));
    setFailedSources(previous => ({ ...previous, [src]: true }));
  };
  const visibleBase = `/assets/home-projects-glass/${variants[visibleTheme]}-v9.png`;
  return (
    <div className={styles.projectsObject} data-glass-theme={theme} data-glass-visible-theme={visibleTheme}
      data-glass-ready={!!ready[visibleBase]} data-glass-failed={!!failedSources[visibleBase]}>
      <span className={styles.projectGlassBase}>
        {Object.entries(variants).map(([id, name]) => {
          const src = `/assets/home-projects-glass/${name}-v9.png`;
          return <img key={id} className={`${styles.projectGlass} ${styles.projectGlassTheme}`} src={src} alt=""
            width={1254} height={1254} draggable={false} decoding="async" fetchPriority={id === theme ? 'high' : 'low'}
            onLoad={loaded} onError={failed}
            data-active={visibleTheme === id && !!ready[src]} aria-hidden="true" />;
        })}
      </span>
      {Object.entries(variants).map(([id, name]) => {
        const src = `/assets/home-projects-glass/${name}-hover-v9.png`;
        return <img key={id} className={`${styles.projectGlass} ${styles.projectGlassLit}`} src={src} alt=""
          width={1254} height={1254} draggable={false} decoding="async" fetchPriority="low" onLoad={loaded} onError={failed}
          data-active={visibleTheme === id && !!ready[src]} aria-hidden="true" />;
      })}
      <div className={styles.projectsSurface}>{children}</div>
    </div>
  );
}

function WallArtwork({ src, className, name, width, height }: {
  src: string; className: string; name?: string; width?: number; height?: number;
}) {
  return (
    <span className={styles.frameArtwork}>
      <img key={src} src={src} alt="" width={width} height={height} loading="lazy" decoding="async" className={className} data-wall-photo={name} />
      <span className={styles.frameIllumination} aria-hidden="true">
        <img key={src} src={src} alt="" width={width} height={height} loading="lazy" decoding="async" className={`${className} ${styles.frameLitImage}`} />
      </span>
    </span>
  );
}

export function DirectionNews({ direction }: { direction: WallPhotoCategory }) {
  const achievements = direction === 'achievements';
  const newsFeed = useNews({ tag: achievements ? achievementNewsTag : directionNewsTags[direction], limit: 3, ordering: '-published_at' });
  // Серверная выборка уже ограничена специальной меткой: каталог проектов здесь не нужен.
  const news = (achievements ? getAchievementNews(newsFeed.news) : getDirectionNews(direction, [], newsFeed.news)).slice(0, 3);
  const photoFeed = useWallPhotos(direction, 3 - news.length);
  const failed = Boolean(newsFeed.error || photoFeed.error);
  const feedsSettled = (!newsFeed.isLoading || Boolean(newsFeed.error)) && (!photoFeed.isLoading || Boolean(photoFeed.error));

  return (
    <section ref={photoFeed.ref} className={styles.newsWall} data-direction-news={direction} aria-labelledby={`${direction}-news-title`} aria-busy={newsFeed.isLoading}>
      <h2 id={`${direction}-news-title`} className={styles.srOnly}>{achievements ? 'Наши достижения' : 'Новости направления'}</h2>
      {news.length > 0 && <Link href="/news" className={styles.wallArchive}>Все новости<UiIcon name="ArrowUpRight" /></Link>}
      {newsFeed.isLoading && !newsFeed.error && <span className={styles.srOnly} role="status">Загружаем новости…</span>}
      <ul className={styles.newsList}>
        {[0, 1, 2].map(slot => {
          const item = news[slot];
          const photo = !item ? photoFeed.photos[slot - news.length] : undefined;
          const timestamp = item && getNewsPublicationTime(item.published_at);
          const published = timestamp != null && Number.isFinite(timestamp) ? new Date(timestamp) : null;
          const title = item ? getNewsDisplayMeta(item).title : undefined;
          const contentKey = JSON.stringify([direction, item?.id, item?.slug, title, item?.cover_url, timestamp,
            photo?.id, photo?.url, photo?.name, photo?.width, photo?.height]);
          // При сбое SWR сохраняет данные и повторяет запрос; без замены оставляем готовый кадр.
          const ready = Boolean(item) || (feedsSettled && (Boolean(photo) || !failed));
          return (
            <li key={slot} className={styles.newsFrame} data-wall-frame={slot}
              style={{ '--frame-left': `${4 + slot * (92 * (1.03 / 3))}cqw` } as CSSProperties}
              aria-hidden={!item ? true : undefined}>
              <WallFrame contentKey={contentKey} ready={ready} textFallback={Boolean(item)}>
                {item && <Link href={`/news/${encodeURIComponent(item.slug.trim())}`} className={styles.newsLink}>
                  {item.cover_url && <WallArtwork src={item.cover_url} className={styles.newsCover} />}
                  <span className={styles.newsCopy}>
                    <span className={styles.newsTitle}>{title}</span>
                    {published && <time dateTime={published.toISOString()}>{published.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Moscow' })}</time>}
                  </span>
                </Link>}
                {photo && <WallArtwork src={photo.url} width={photo.width} height={photo.height} className={styles.framePhoto} name={photo.name} />}
              </WallFrame>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
